import type { AbilityTrigger, Card } from './cards/types';

// Keyword glossary — the rules text the game authors but never explains.
//
// Per docs/tooltip-audit.md §2.3: `Card.flags` carries firstStrike,
// divineShield, poisonous, reborn, windfury, cleave and overkill, and a grep
// for a glossary returned nothing. A card's `text` string was the only
// explanation and it assumes the reader already knows the keyword. Same for
// `Card.abilities[].trigger` — the timing was never named to the player.
//
// Scope is deliberately flags + triggers, not the ~30 ability ids in
// abilityEngine. Ability ids are implementation detail; the card's own `text`
// already says what it does. What players can't infer is what "Last Breath"
// or "Poisonous" MEAN.
//
// Depth budget from the audit: rule lines stay one sentence. Anything longer
// belongs in the card detail body, not a keyword line.

export interface Keyword {
  /** Display name, title case, as it should appear to the player. */
  name: string;
  /** One-sentence rule. No jargon that isn't itself a listed keyword. */
  rule: string;
}

/** Keys of `Card.flags` that are player-facing mechanics. */
export type KeywordFlag =
  | 'firstStrike'
  | 'cannotAttack'
  | 'divineShield'
  | 'poisonous'
  | 'reborn'
  | 'windfury'
  | 'cleave'
  | 'overkill';

export const FLAG_KEYWORDS: Record<KeywordFlag, Keyword> = {
  firstStrike: {
    name: 'First Strike',
    rule: 'Strikes before the enemy it fights, so a lethal hit lands unanswered.',
  },
  cannotAttack: {
    name: "Can't Attack",
    rule: 'Never attacks. It holds a slot and does its work through abilities and auras.',
  },
  divineShield: {
    name: 'Divine Shield',
    rule: 'Ignores the next damage it would take, however large, then the shield breaks.',
  },
  poisonous: {
    name: 'Poisonous',
    rule: 'Any unit it damages dies, no matter how much Health is left.',
  },
  reborn: {
    name: 'Reborn',
    rule: 'The first time it dies it returns in the same fight with 1 Health.',
  },
  windfury: {
    name: 'Windfury',
    rule: 'Attacks twice each time its turn to attack comes around.',
  },
  cleave: {
    name: 'Cleave',
    rule: 'Also damages the units either side of the one it attacks.',
  },
  overkill: {
    name: 'Overkill',
    rule: 'Damage past a kill spills into the next enemy, and keeps spilling while damage remains.',
  },
};

export const TRIGGER_KEYWORDS: Record<AbilityTrigger, Keyword> = {
  on_buy: {
    name: 'On Buy',
    rule: 'Fires the moment you buy it from the shop.',
  },
  on_sell: {
    name: 'On Sell',
    rule: 'Fires when you sell it.',
  },
  summon: {
    name: 'Summon',
    rule: 'Fires when it lands on your board.',
  },
  lastbreath: {
    name: 'Last Breath',
    rule: 'Fires when it dies.',
  },
  start_of_combat: {
    name: 'Start of Combat',
    rule: 'Fires once, before the first attack of the fight.',
  },
  start_of_turn: {
    name: 'Start of Turn',
    rule: 'Fires when a new tavern turn opens, before you spend anything.',
  },
  end_of_turn: {
    name: 'End of Turn',
    rule: 'Fires when you end your tavern turn, before the fight.',
  },
  on_friendly_death: {
    name: 'On Friendly Death',
    rule: 'Fires each time another of your units dies.',
  },
  on_take_damage: {
    name: 'On Damage',
    rule: 'Fires when it survives being damaged.',
  },
  after_combat_survives: {
    name: 'Survives Combat',
    rule: 'Fires after a fight it lived through.',
  },
  on_deal_damage: {
    name: 'On Damage Dealt',
    rule: 'Fires each time its attack connects.',
  },
  on_first_attack: {
    name: 'First Swing',
    rule: 'Fires once a fight, on the opening attack of your side.',
  },
  avenge: {
    name: 'Avenge',
    rule: 'Fires once a set number of your units have died this fight.',
  },
  passive: {
    name: 'Passive',
    rule: 'Always on — it changes how a rule applies rather than reacting.',
  },
};

/** Flags set on this card, in a stable display order. */
export function keywordsForCard(card: Card): Keyword[] {
  if (!card.flags) return [];
  const order: KeywordFlag[] = [
    'firstStrike',
    'windfury',
    'cleave',
    'overkill',
    'poisonous',
    'divineShield',
    'reborn',
    'cannotAttack',
  ];
  return order
    .filter((k) => card.flags?.[k] === true)
    .map((k) => FLAG_KEYWORDS[k]);
}

/** Distinct ability triggers on this card, in the order they're declared. */
export function triggersForCard(card: Card): Keyword[] {
  if (!card.abilities?.length) return [];
  const seen = new Set<AbilityTrigger>();
  const out: Keyword[] = [];
  for (const a of card.abilities) {
    if (seen.has(a.trigger)) continue;
    seen.add(a.trigger);
    const kw = TRIGGER_KEYWORDS[a.trigger];
    if (kw) out.push(kw);
  }
  return out;
}
